import React from 'react';
import { InputNumber } from 'antd';
import { Settings, Info } from 'lucide-react';
import { 
    fmtCur, fmtCurNoDec, fmtNum, fmtPts, formatCurrencyExt 
} from '../Common';

const GamePlanTab = ({ gpParams, setGpParams, gpResults, syncInputs }) => {
    const { capital, targetReturn, lotSize, marginPerLot, lots, winPts, lossPts, winRate, riskPct } = gpParams;
    const results = gpResults;

    return (
        <>
        <div className="gpt-grid">
            <div className="gpt-panel">
                <div className="gpt-panel-title"><Settings size={18} /> Strategy Params</div>
                <div className="gpt-input-row flex-wrap">
                    <div className="gpt-input-group">
                        <div className="gpt-input-label">Capital</div>
                        <div className="gpt-input-wrapper">
                            <InputNumber
                                prefix="₹"
                                style={{ width: '100%' }}
                                value={capital}
                                onChange={(val) => syncInputs('gp', val || 0, 'capital')}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value.replace(/\₹\s?|(,*)/g, '')}
                            />
                        </div>
                    </div>
                </div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">Target Return</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            suffix="%"
                            style={{ width: '100%' }}
                            value={targetReturn}
                            onChange={(val) => syncInputs('gp', val || 0, 'targetReturn')}
                        />
                    </div>
                </div>
                <div className="gpt-divider" data-label="Trading Size"></div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">NIFTY Lot Size</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            style={{ width: '100%' }}
                            value={lotSize}
                            onChange={(val) => setGpParams(prev => ({ ...prev, lotSize: val || 0 }))}
                        />
                    </div>
                </div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">Margin Required Per Lot</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            prefix="₹"
                            style={{ width: '100%' }}
                            value={marginPerLot}
                            onChange={(val) => setGpParams(prev => ({ ...prev, marginPerLot: val || 0 }))}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        />
                    </div>
                </div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">Lots to Trade</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            style={{ width: '100%' }}
                            value={lots}
                            onChange={(val) => setGpParams(prev => ({ ...prev, lots: val || 0 }))}
                        />
                    </div>
                </div>
                <div className="gpt-divider" data-label="Risk & Edge"></div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">Avg Winner (Points)</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            style={{ width: '100%' }}
                            value={winPts}
                            onChange={(val) => setGpParams(prev => ({ ...prev, winPts: val || 0 }))}
                        />
                    </div>
                </div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">Avg Loser / SL (Points)</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            style={{ width: '100%' }}
                            value={lossPts}
                            onChange={(val) => setGpParams(prev => ({ ...prev, lossPts: val || 0 }))}
                        />
                    </div>
                </div>
                <div className="gpt-input-group">
                    <div className="gpt-input-label">Win Rate</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            suffix="%"
                            style={{ width: '100%' }}
                            value={winRate}
                            onChange={(val) => setGpParams(prev => ({ ...prev, winRate: val || 0 }))}
                        />
                    </div>
                </div>
                <div className="gpt-input-group no-margin">
                    <div className="gpt-input-label">Risk Per Trade</div>
                    <div className="gpt-input-wrapper">
                        <InputNumber
                            suffix="%"
                            step={0.1}
                            style={{ width: '100%' }}
                            value={riskPct}
                            onChange={(val) => setGpParams(prev => ({ ...prev, riskPct: val || 0 }))}
                        />
                    </div>
                </div>
            </div>

            <div className="gpt-results">
                <div className="gpt-results-grid">
                    <div className="gpt-card">
                        <div className="gpt-card-header">
                            <div className="gpt-step">1</div>
                            <div className="gpt-card-title">Base Numbers</div>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Capital Base</span>
                            <span className="gpt-value">{formatCurrencyExt(capital)}</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Target Return</span>
                            <span className="gpt-value val-blue">{targetReturn}%</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Annual Profit Req.</span>
                            <span className="gpt-value val-green big-value">{formatCurrencyExt(results.annualProfit)}</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Value Per Pt (1 Lot)</span>
                            <span className="gpt-value">₹{lotSize}</span>
                        </div>
                    </div>

                    <div className="gpt-card">
                        <div className="gpt-card-header">
                            <div className="gpt-step">2</div>
                            <div className="gpt-card-title">Sensible Sizing</div>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Trading Lots</span>
                            <span className="gpt-value val-purple big-value">{lots} Lots</span>
                        </div>
                        <div className="gpt-formula">{lotSize} lot size × {lots} lots</div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Active Value Per Point</span>
                            <span className="gpt-value val-blue big-value">₹{results.valPerPt}</span>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '12px', textAlign: 'center' }}>
                            <p className='mb-2'>Professional utilization is usually ~40-60% of total capital.</p>
                            <strong style={{ color: 'var(--text)' }}>{results.minSuggestedLots} to {results.maxSuggestedLots} Lots recommended.</strong>
                        </div>
                    </div>

                    <div className="gpt-card">
                        <div className="gpt-card-header">
                            <div className="gpt-step">3</div>
                            <div className="gpt-card-title">Points Needed Velocity</div>
                        </div>
                        <div className="gpt-points">
                            <div className="gpt-point-box">
                                <div className="gpt-pb-label">Yearly</div>
                                <div className="gpt-pb-value">{fmtCurNoDec.format(results.ptsYear)} <span>pts</span></div>
                            </div>
                            <div className="gpt-point-box">
                                <div className="gpt-pb-label">Monthly</div>
                                <div className="gpt-pb-value">{Math.ceil(results.ptsMonth || 0)} <span>pts</span></div>
                            </div>
                            <div className="gpt-point-box">
                                <div className="gpt-pb-label">Weekly</div>
                                <div className="gpt-pb-value">{Math.ceil(results.ptsWeek || 0)} <span>pts</span></div>
                            </div>
                            <div className="gpt-point-box success">
                                <div className="gpt-pb-label">Daily Requires Only</div>
                                <div className="gpt-pb-value">{fmtPts.format(results.ptsDay)} <span>pts/day</span></div>
                            </div>
                        </div>
                    </div>

                    <div className="gpt-card">
                        <div className="gpt-card-header">
                            <div className="gpt-step">4 & 5</div>
                            <div className="gpt-card-title">Realistic Trade Model</div>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Target Move</span>
                            <span className="gpt-value">{winPts} points</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Profit / Winner</span>
                            <span className="gpt-value val-green">{formatCurrencyExt(results.profitWinner)}</span>
                        </div>
                        <div className="gpt-formula">{winPts} pts × ₹{results.valPerPt}</div>
                        <div className="gpt-data-row" style={{ marginTop: '16px' }}>
                            <span className="gpt-label">Stoploss Move</span>
                            <span className="gpt-value">{lossPts} points</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Loss / Loser</span>
                            <span className="gpt-value val-red">-{formatCurrencyExt(results.lossLoser)}</span>
                        </div>
                        <div className="gpt-formula">{lossPts} pts × ₹{results.valPerPt}</div>
                    </div>

                    <div className="gpt-card">
                        <div className="gpt-card-header">
                            <div className="gpt-step">6</div>
                            <div className="gpt-card-title">Statistical Edge (EV)</div>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Win Rate Assumed</span>
                            <span className="gpt-value">{winRate}%</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Risk : Reward</span>
                            <span className="gpt-value val-amber">1 : {fmtNum.format(results.rr || 0)}</span>
                        </div>
                        <div className="gpt-formula">EV = ({results.wProb?.toFixed(2)}×{winPts}) - ({results.lProb?.toFixed(2)}×{lossPts})</div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">EV Points / Trade</span>
                            <span className={`gpt-value ${results.evPts >= 0 ? 'val-green' : 'val-red'}`}>
                                {results.evPts >= 0 ? '+' : ''}{fmtPts.format(results.evPts)} pts
                            </span>
                        </div>
                        <div className={`gpt-hb-container ${results.evPts >= 0 ? 'hb-green' : 'hb-amber'}`}>
                            <div className="gpt-hb-label">Expected Value / Trade</div>
                            <div className={`gpt-hb-value ${results.evPts >= 0 ? 'val-green' : 'val-red'}`}>{formatCurrencyExt(results.evMoney)}</div>
                            <div className="gpt-hb-sub">Consistent edge over large sample size.</div>
                        </div>
                    </div>

                    <div className="gpt-card">
                        <div className="gpt-card-header">
                            <div className="gpt-step">7</div>
                            <div className="gpt-card-title">Professional Risk Model</div>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Risk % Per Trade</span>
                            <span className="gpt-value val-red">{riskPct}%</span>
                        </div>
                        <div className="gpt-data-row">
                            <span className="gpt-label">Max Risk Amount</span>
                            <span className="gpt-value">{formatCurrencyExt(results.riskAmt)}</span>
                        </div>
                        <div className="gpt-formula">{formatCurrencyExt(capital)} × {riskPct}%</div>
                        <div className="gpt-hb-container hb-amber" style={{ marginTop: '24px' }}>
                            <div className="gpt-hb-label">Max Allowable Stoploss</div>
                            <div className="gpt-hb-value">{results.maxSlPts} pts</div>
                            <div className="gpt-hb-sub">{formatCurrencyExt(results.riskAmt)} / ₹{results.valPerPt} per pt</div>
                        </div>
                    </div>

                    <div className="gpt-card wide">
                        <div className="gpt-card-header">
                            <div className="gpt-step accent">8</div>
                            <div className="gpt-card-title">What This Means In Practice?</div>
                        </div>
                        <div className="gpt-summary-content">
                            <div className="gpt-summary-text">
                                To make <strong style={{ color: 'var(--text)', fontSize: '16px' }}>{targetReturn}%</strong> yearly, you ONLY need to capture:<br />
                                <span className="gpt-points-highlight me-1">~{Math.ceil(results.ptsDay || 0)} points</span>per day.<br /><br />
                                Compared to NIFTY's average daily intraday range of <strong>120 to 180 points</strong>, your strategy only needs to capture around...
                            </div>
                            <div className="gpt-hb-container hb-blue summary-hb">
                                <div className="gpt-hb-label">Required Capture Rate</div>
                                <div className="gpt-hb-value">~{fmtPts.format(results.captureRate)}%</div>
                                <div className="gpt-hb-sub">of the daily movement</div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
                <div className="gpt-description-panel">
                    <div className="gpt-desc-header">
                        <h2 className="gpt-desc-title">Trading Game Plan</h2>
                    </div>
                    <span className="gpt-desc-meta">Turning Your North Star Goal Into Daily Action</span>
                    <div className="gpt-desc-divider"></div>

                    <p className="gpt-desc-content">The Trading Game Plan tool transforms a long-term trading goal into a clear and measurable roadmap. Instead of focusing only on yearly targets like “50% annual return,” the system breaks the goal into smaller, achievable milestones that can be tracked daily, weekly, and monthly.</p>

                    <h4 className="gpt-desc-subtitle">What is the North Star Goal</h4>
                    <p className="gpt-desc-content">Your North Star is the long-term capital target you want to achieve through trading.</p>

                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">For example:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">STARTING CAPITAL</div>
                                <div className="gpt-ex-value">₹50,00,000</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">TARGET RETURN</div>
                                <div className="gpt-ex-value">50%</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="gpt-ex-label">ANNUAL GOAL</div>
                                <div className="gpt-ex-value val-blue">₹25,00,000</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">Instead of leaving this goal abstract, the Game Plan tool converts it into specific numbers that guide your daily trading decisions.</p>

                    <h4 className="gpt-desc-subtitle">Breaking Down the Goal Into Achievable Targets</h4>
                    <p className="gpt-desc-content">The system calculates how much progress must be made at different time intervals to stay on track.</p>
                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">Example breakdown:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">ANNUAL REQUIREMENT</div>
                                <div className="gpt-ex-value">₹25,00,000</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">MONTHLY REQUIREMENT</div>
                                <div className="gpt-ex-value">₹2,08,333</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">WEEKLY REQUIREMENT</div>
                                <div className="gpt-ex-value">₹48,000</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="gpt-ex-label">DAILY REQUIREMENT</div>
                                <div className="gpt-ex-value val-green">₹12,800</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">By converting the goal into smaller checkpoints, traders gain clarity about what needs to be achieved each day.</p>

                    <h4 className="gpt-desc-subtitle">Points Capture Requirement</h4>
                    <p className="gpt-desc-content">For futures traders, performance is often measured in points captured rather than just monetary profit. The Game Plan tool calculates how many points need to be captured to achieve the yearly return goal.</p>
                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">Example:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">ACTIVE VALUE PER POINT</div>
                                <div className="gpt-ex-value">₹780</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">YEARLY POINTS REQUIRED</div>
                                <div className="gpt-ex-value">3205 points</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">MONTHLY POINTS REQUIRED</div>
                                <div className="gpt-ex-value">268 points</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">WEEKLY POINTS REQUIRED</div>
                                <div className="gpt-ex-value">65 points</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="gpt-ex-label">DAILY REQUIREMENT</div>
                                <div className="gpt-ex-value val-green">12.8 points per day</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">This provides a simple performance benchmark that traders can track consistently.</p>

                    <h4 className="gpt-desc-subtitle">Realistic Trade Model</h4>
                    <p className="gpt-desc-content">The tool also estimates what a typical trade might look like based on your strategy assumptions.</p>
                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">Example setup:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">TARGET MOVE</div>
                                <div className="gpt-ex-value">15 points</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">STOPLOSS</div>
                                <div className="gpt-ex-value">10 points</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">PROFIT PER WINNING TRADE</div>
                                <div className="gpt-ex-value val-green">₹11,700</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">LOSS PER LOSING TRADE</div>
                                <div className="gpt-ex-value val-red">₹7,800</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">This allows traders to understand whether their strategy aligns with the required performance levels.</p>

                    <h4 className="gpt-desc-subtitle">Understanding the Statistical Edge</h4>
                    <p className="gpt-desc-content">A sustainable trading plan requires a positive statistical edge. The Game Plan incorporates key parameters such as:</p>
                    <ul className="gpt-desc-list">
                        <li>Win rate</li>
                        <li>Average winning trade size</li>
                        <li>Average losing trade size</li>
                    </ul>
                    <p className="gpt-desc-content">From these inputs, the system calculates the expected value per trade, which represents the average profit expected over many trades.</p>

                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">Example:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">WIN RATE</div>
                                <div className="gpt-ex-value">45%</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">RISK REWARD RATIO</div>
                                <div className="gpt-ex-value">1 : 1.5</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="gpt-ex-label">EXPECTED VALUE</div>
                                <div className="gpt-ex-value val-green">+1.3 points per trade</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">A positive expected value indicates that the trading strategy has a mathematical advantage over time.</p>

                    <h4 className="gpt-desc-subtitle">Professional Risk Management</h4>
                    <p className="gpt-desc-content">Risk management is one of the most critical aspects of achieving long-term trading success. The Game Plan tool calculates the maximum allowable risk per trade based on the trader’s capital.</p>

                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">Example:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">CAPITAL</div>
                                <div className="gpt-ex-value">₹50,00,000</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">RISK PER TRADE</div>
                                <div className="gpt-ex-value">1%</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="gpt-ex-label">MAXIMUM RISK AMOUNT</div>
                                <div className="gpt-ex-value val-red">₹50,000</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">Based on the active value per point, the system also calculates the maximum stoploss that can be used without exceeding the defined risk limit. This ensures that every trade remains within a controlled risk framework.</p>

                    <h4 className="gpt-desc-subtitle">Required Capture Rate</h4>
                    <p className="gpt-desc-content">One of the most powerful insights the Game Plan provides is the required capture rate relative to the market’s daily movement.</p>

                    <div className="gpt-desc-example">
                        <p className="gpt-example-label">Example:</p>
                        <div className="gpt-example-grid">
                            <div>
                                <div className="gpt-ex-label">AVERAGE NIFTY DAILY RANGE</div>
                                <div className="gpt-ex-value">120 to 180 points</div>
                            </div>
                            <div>
                                <div className="gpt-ex-label">REQUIRED CAPTURE</div>
                                <div className="gpt-ex-value">12.8 points per day</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="gpt-ex-label">CAPTURE RATE</div>
                                <div className="gpt-ex-value val-blue">approximately 8.5% of the daily range</div>
                            </div>
                        </div>
                    </div>
                    <p className="gpt-desc-content">This shows that achieving the yearly goal does not require capturing large market moves. Instead, it requires consistent execution and disciplined risk management.</p>

                    <div className="gpt-desc-divider" style={{ margin: '40px 0' }}></div>

                    <h4 className="gpt-desc-subtitle">Why Breaking Goals Into Smaller Targets Works</h4>
                    <p className="gpt-desc-content">Large goals often feel overwhelming and difficult to track. By breaking the North Star goal into smaller targets, the Game Plan helps traders focus on:</p>
                    <ul className="gpt-desc-list">
                        <li>Daily consistency</li>
                        <li>Risk control</li>
                        <li>Systematic execution</li>
                    </ul>
                    <p className="gpt-desc-content">Small, repeatable progress accumulates into large long-term results through compounding.</p>

                    <h4 className="gpt-desc-subtitle">The Psychological Advantage</h4>
                    <p className="gpt-desc-content">Traders often make emotional decisions because they focus only on daily profits and losses. The Game Plan shifts the focus toward trajectory and consistency. A losing day does not necessarily mean the trader is failing. What matters is whether performance remains aligned with the long-term plan.</p>
                    <p className="val-text-strong" style={{ fontWeight: 600, marginTop: '16px' }}>This perspective encourages discipline and reduces emotional trading.</p>

                    <h4 className="gpt-desc-subtitle">Who This Tool Is Designed For</h4>
                    <p className="gpt-desc-content">The Game Plan tool is particularly useful for:</p>
                    <ul className="gpt-desc-list">
                        <li>Intraday futures traders</li>
                        <li>Options traders</li>
                        <li>Systematic strategy traders</li>
                        <li>Professional and aspiring prop traders</li>
                    </ul>
                    <p className="gpt-desc-content">Anyone who wants to approach trading with a structured, data-driven mindset can benefit from this framework.</p>

                    <div className="gpt-hb-container hb-green" style={{ marginTop: '48px', padding: '32px', textAlign: 'center' }}>
                        <h4 className="gpt-hb-title" style={{ marginTop: 0, marginBottom: '20px', fontSize: '24px', fontWeight: 700 }}>The Core Idea</h4>
                        <p className="gpt-desc-content" style={{ marginBottom: '20px' }}>Successful trading is not about chasing large wins. It is about consistently executing a well-defined plan. The Game Plan tool ensures that traders always know:</p>
                        <p className="gpt-core-point" style={{ fontWeight: 500, fontSize: '18px', color: 'var(--text)', margin: '8px 0' }}>where they started</p>
                        <p className="gpt-core-point" style={{ fontWeight: 500, fontSize: '18px', color: 'var(--text)', margin: '8px 0' }}>where they need to go</p>
                        <p className="gpt-core-point" style={{ fontWeight: 500, fontSize: '18px', color: 'var(--text)', margin: '8px 0' }}>and what must be achieved each day to reach their North Star goal.</p>
                    </div>
                </div>
        </>
    );
};

export default GamePlanTab;
